import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import { transactionFooter } from '@orn/shared-web/lib/transaction/transactionDisplay';
import formatAmountRangeLabel from '@orn/shared-web/utils/money/formatAmountRangeLabel';
import toCurrency from '@orn/shared-web/utils/money/toCurrency';

import { entityLogoSrc } from '../../components/entity/EntityAvatar/EntityAvatar';
import { extractErrorCode } from '../../utils/apollo/error';
import { formatCalendarDate } from '../../utils/formatting';

import type { GetRecurringTransactionPageQuery } from './__generated__/RecurringTransactionPane.gql';
import type { CalendarDate } from '@internationalized/date';
import type { GraphQLFormattedError } from 'graphql';

export type RecurringTransactionPageRecord = GetRecurringTransactionPageQuery['getRecurringTransaction'];

/** The account a hero cell draws: the stream's own, or the last payment's. */
export type RecurringTransactionPageAccount = RecurringTransactionPageRecord['financialAccount'];

/** Which payment the hero describes: the one that happened, or the one predicted. */
export type RecurringTransactionHeroMode = 'last-payment' | 'expected-next';

export interface IRecurringTransactionHero {
  mode: RecurringTransactionHeroMode;
  name: string;
  logoSrc: string | null;
  /** Already formatted — the last payment, or the typical range. */
  amount: string;
  /** The timing tag's date, `'inactive'` for the Inactive pill, or null for no pill. */
  pill: CalendarDate | 'inactive' | null;
  /** The Date cell's value, or null to omit the cell. */
  dateLabel: string | null;
  /** The Account cell's account, or null to omit the cell. */
  account: RecurringTransactionPageAccount | null;
  /** The last payment's raw description, when it says more than the name does. */
  footer: string | null;
}

/** Printed when the stream has never priced an occurrence, rather than a fictional "$0". */
const NO_AMOUNT = '—';

/**
 * What the record page's hero shows for a stream.
 *
 * A stream with a payment behind it shows that payment (its amount, date, and
 * account); one without shows the expected next payment (the typical range
 * and the predicted date). Predictions render only for an active stream: an
 * inactive one keeps whichever layout applies, wears the Inactive pill, and
 * drops the expected date. The timing pill is also withheld when the
 * prediction predates the last payment — the stream row lags the transaction
 * sync, and "Past expected date" above a newer payment would contradict
 * itself.
 */
export function buildRecurringTransactionHero(stream: RecurringTransactionPageRecord): IRecurringTransactionHero {
  const { lastTransaction, isActive, nextOccurrence, currencyCode } = stream;
  const name = stream.merchant?.name ?? stream.description;
  const logoSrc = stream.merchant ? entityLogoSrc(stream.merchant) : null;
  const prediction = isActive ? (nextOccurrence ?? null) : null;

  if (lastTransaction) {
    const isStalePrediction = prediction !== null && prediction.compare(lastTransaction.date) <= 0;
    return {
      mode: 'last-payment',
      name,
      logoSrc,
      amount: toCurrency(Math.abs(Number(lastTransaction.amount)), { currencyCode }),
      pill: isActive ? (isStalePrediction ? null : prediction) : 'inactive',
      dateLabel: formatCalendarDate(lastTransaction.date, 'medium'),
      account: lastTransaction.financialAccount ?? stream.financialAccount,
      footer: transactionFooter(lastTransaction.rawDescription, name),
    };
  }

  return {
    mode: 'expected-next',
    name,
    logoSrc,
    amount: formatAmountRangeLabel(stream.amountRange, currencyCode) ?? NO_AMOUNT,
    pill: isActive ? prediction : 'inactive',
    dateLabel: prediction ? `Expected ${formatCalendarDate(prediction, 'medium')}` : null,
    account: stream.financialAccount,
    footer: null,
  };
}

interface IRecurringTransactionPageRead {
  /** The route's id; empty when a caller rendered the pane without one. */
  id: string;
  data: GetRecurringTransactionPageQuery | undefined;
  loading: boolean;
  error: { graphQLErrors?: readonly GraphQLFormattedError[] } | undefined;
}

export type RecurringTransactionPageState =
  | { status: 'ready'; stream: RecurringTransactionPageRecord }
  | { status: 'loading' | 'error' | 'not-found' };

/**
 * The shell state for a page read. `getRecurringTransaction` is non-nullable
 * and answers a missing id with `RECURRING_STREAM_NOT_FOUND`, so not-found is
 * read off the error's code rather than off a null record; any other failure
 * is the shell's error state. An empty id never issued the read.
 */
export function resolveRecurringTransactionPage({
  id,
  data,
  loading,
  error,
}: IRecurringTransactionPageRead): RecurringTransactionPageState {
  if (!id) {
    return { status: 'not-found' };
  }
  if (error) {
    const isNotFound = extractErrorCode(error.graphQLErrors ?? []) === ApiErrorCode.RECURRING_STREAM_NOT_FOUND;
    return { status: isNotFound ? 'not-found' : 'error' };
  }
  if (data?.getRecurringTransaction) {
    return { status: 'ready', stream: data.getRecurringTransaction };
  }
  return { status: loading ? 'loading' : 'error' };
}
